/**
 * Hatched Widget Loader
 *
 * Minimal loader script that partner sites include via <script> tag.
 * Reads configuration from data attributes, creates Shadow DOM containers,
 * and lazy-loads the appropriate widget bundles.
 *
 * Usage:
 *   <script
 *     src="https://cdn.hatched.live/widget.js"
 *     data-embed-token="tok_abc123"
 *     data-theme="light"
 *     data-api-base-url="https://api.hatched.live/api/v1"
 *   ></script>
 *   <div data-hatched-mount="buddy"></div>
 *   <div data-hatched-mount="marketplace"></div>
 *   <div data-hatched-mount="leaderboard"></div>
 *
 * Hot-swapping: call window.__HATCHED_WIDGET__.init({ token }) again with a
 * different token and the loader will tear down each widget's shadow content
 * and re-mount with the new token. This lets a host page (e.g. /demo/try)
 * exchange a placeholder-less state for a real token after a /demo/hatch call
 * without reloading.
 *
 * Phase 0 realtime: the loader also owns the shared WorkspaceStore, starts a
 * smart poll against /widget/state, watches async ops, bridges to other tabs,
 * and exposes `track(type, payload)` on the global API. Individual widgets
 * subscribe to the store instead of fetching on their own 30s timers.
 */

// Type-only import: erased at build time. The loader ships as a single IIFE
// partners include on every page — a *value* import of @hatched/shared (a
// CommonJS barrel that pulls in zod via ./contracts) would inline the whole
// package into the bundle. The registry below therefore keeps literal
// entries, but every field is type-checked against the shared catalog and
// the drift guards next to WIDGET_REGISTRY turn a missing/extra/renamed
// widget into a compile error.
import type {
  WidgetCatalogEntry,
  WidgetId as CatalogWidgetId,
} from '@hatched/shared';
import { WorkspaceStore, type EventEffects, type WorkspaceState } from '../shared/store';
import { startSmartPoll, type SmartPollHandle } from '../shared/smart-poll';
import { startAsyncOpsWatcher } from '../shared/async-ops-watcher';
import { attachCrossTabBridge, type CrossTabBridge } from '../shared/broadcast-channel';
import { listSupportedLocales } from '../shared/i18n';
import {
  ALLOWED_API_ORIGINS,
  inferDefaultApiBaseUrl,
  isAllowedApiBaseUrl,
} from './api-base-url';

interface WidgetPersonalityConfig {
  surface: 'paper' | 'glass' | 'metal' | 'crt' | 'parchment' | 'vellum';
  geometry: 'rounded' | 'cut' | 'pill' | 'sharp' | 'organic';
  motion_profile: 'calm' | 'standard' | 'expressive' | 'theatrical';
  reward_voice: 'quiet' | 'crisp' | 'joyful' | 'epic';
  iconography: 'geometric' | 'hand-drawn' | 'pixel' | 'embossed' | 'flat-mono';
  typography_pair:
    | 'sans-serif-modern'
    | 'serif-classic'
    | 'mono-tech'
    | 'rounded-playful'
    | 'display-condensed';
}

interface HatchedWidgetConfig {
  token: string;
  theme: 'light' | 'dark';
  mode: 'interactive' | 'read-only';
  apiBaseUrl: string;
  cdnBaseUrl: string;
  size?: 'small' | 'medium' | 'large';
  lang?: string;
  themeVars?: Record<string, string>;
  customCss?: string;
  personality?: WidgetPersonalityConfig;
  /**
   * Per-mount data-* attributes forwarded from the <div>. A widget that
   * supports multiple instances on one page (e.g. streak) reads its
   * instance identity from here — for streak it's `mount.streakKey`.
   */
  mount?: Record<string, string>;
}

interface TrackResult {
  accepted: boolean;
  event_id: string;
  effects: EventEffects;
  duplicate?: boolean;
  unmatched?: boolean;
}

interface AggregateState {
  buddy: WorkspaceState['buddy'];
  badges: WorkspaceState['badges'];
  pending_operations: Array<{
    id: string;
    type: 'hatch' | 'evolve' | 'equip_item';
    status: 'pending' | 'processing';
    updated_at: string;
  }>;
  scopes?: string[];
  attribution?: WorkspaceState['attribution'];
  share?: WorkspaceState['share'];
  feature_config?: Record<string, unknown>;
}

type WidgetCleanup = () => void;

interface WidgetModule {
  mount?: (container: HTMLElement, config: HatchedWidgetConfig) => void | WidgetCleanup;
}

interface CelebrationBadgeInput {
  key: string;
  label: string;
  description?: string | null;
  icon_url?: string | null;
  coin_reward?: number;
}

type CelebrationInput =
  | {
      kind: 'badge';
      badge: CelebrationBadgeInput;
      earnedCountBefore?: number;
      tier?: 'spark' | 'pulse' | 'bloom' | 'ceremony';
    }
  | {
      kind: 'evolution';
      fromStage: number;
      toStage: number;
      imageUrl: string;
      tier?: 'spark' | 'pulse' | 'bloom' | 'ceremony';
    }
  | {
      kind: 'streak-milestone';
      definitionKey: string;
      label: string;
      icon: string;
      current: number;
      milestone: number;
      hero?: boolean;
      tier?: 'spark' | 'pulse' | 'bloom' | 'ceremony';
    }
  | {
      kind: 'path-step-completed';
      pathKey: string;
      stepKey: string;
      rewardCoins?: number;
      tier?: 'spark' | 'pulse' | 'bloom' | 'ceremony';
    }
  | {
      // Mirrors PrestigeCelebration in shared/celebrate/types.ts. Kept in sync
      // so prestige events queued before the celebrate-host bundle resolves are
      // not silently untyped at the loader boundary.
      kind: 'prestige';
      prestigeLevel: number;
      fromStage: number;
      fromImageUrl: string | null;
      toImageUrl: string;
      auraTint: 'none' | 'silver' | 'gold' | 'rainbow';
      buddyName?: string;
      tier?: 'spark' | 'pulse' | 'bloom' | 'ceremony';
    };

interface CelebrateHostModule {
  celebrate: (event: CelebrationInput) => void;
  ensureCelebrationHost: (
    theme?: 'light' | 'dark',
    themeVars?: Record<string, string>,
  ) => Promise<unknown>;
}

interface HatchedWidgetAPI {
  config: HatchedWidgetConfig;
  mounted: Set<string>;
  store: WorkspaceStore;
  init: (overrides?: Partial<HatchedWidgetConfig>) => void;
  destroy: () => void;
  celebrate: (event: CelebrationInput) => void;
  /**
   * Fire an event from the browser. Applies an optimistic patch to the
   * store so widgets update before the HTTP round-trip, then reconciles
   * with the server's authoritative effects.
   */
  track: (type: string, payload?: Record<string, unknown>) => Promise<TrackResult | null>;
}

declare const __HATCHED_BUILD_ID__: string;
declare const __HATCHED_LOADER_VERSION__: string;

/**
 * Public introspection surface exposed at `window.HatchedWidgets`. Lets
 * tenants discover available widgets, supported locales, and trigger
 * mount/unmount programmatically without touching `__HATCHED_WIDGET__`
 * (which is reserved for legacy callers + internal hot-swap flows).
 */
export interface HatchedWidgetsPublicAPI {
  /**
   * Semver of the loader bundle that's currently executing (e.g. `0.4.2`).
   * Useful when a partner pins to `cdn.hatched.live/v/<version>/widget.js`
   * and wants to assert the pin took effect.
   */
  readonly version: string;
  /**
   * Opaque build identifier baked into this loader at build time. Changes
   * on every deploy even when the public version stays the same. Use it
   * to correlate field reports with a specific deploy.
   */
  readonly buildId: string;
  /** List the widgets the loader can render. */
  list(): Array<{ name: string; mountAttr: string; prefixMatch: boolean }>;
  /** List the locales the loader ships translations for. */
  listLocales(): readonly string[];
  /** Re-run mount discovery after dynamically inserting mount elements. */
  remount(overrides?: Partial<HatchedWidgetConfig>): void;
  /** Tear down every mounted widget (cleanup before SPA navigation). */
  unmountAll(): void;
}

declare global {
  interface Window {
    __HATCHED_WIDGET__: HatchedWidgetAPI;
    HatchedWidgets: HatchedWidgetsPublicAPI;
  }
}

/**
 * Append the loader's build id as a cache-busting query param to a bundle
 * URL. The bundle filenames are stable (`buddy-widget.min.js`) so partner
 * CDN policies can cache them for a long time, but a new loader release
 * forces every browser to fetch fresh bundles. This is the bridge between
 * "loader is short-cached, bundles are long-cached" and "we can still ship
 * a security patch and have it reach every partner today".
 */
function withBuildId(url: string): string {
  return url.includes('?')
    ? `${url}&v=${__HATCHED_BUILD_ID__}`
    : `${url}?v=${__HATCHED_BUILD_ID__}`;
}

/**
 * Legacy element-id alias for a catalog widget — the pre-`data-hatched-mount`
 * contract older snippets still use (`<div id="buddy-widget">`).
 */
type LegacyWidgetDomId<Id extends CatalogWidgetId> = Id extends 'buddy'
  ? 'buddy-widget'
  : `buddy-${Id}`;

/** The CDN bundle filename the shared catalog declares for a widget. */
type CatalogBundleFor<Id extends CatalogWidgetId> = Extract<
  WidgetCatalogEntry,
  { id: Id }
>['bundle'];

interface WidgetRegistrationFor<Id extends CatalogWidgetId> {
  /** Legacy element-id alias, see {@link LegacyWidgetDomId}. */
  id: LegacyWidgetDomId<Id>;
  bundle: CatalogBundleFor<Id>;
  /** Public mount name used by partner HTML, e.g. data-hatched-mount="buddy". */
  mount: Id;
  /** If true, match every element whose id starts with this widget id. */
  prefixMatch: boolean;
}

/**
 * Union of valid registrations — one shape per shared-catalog widget, so an
 * entry with a mount/bundle pairing the catalog doesn't declare won't compile.
 */
type WidgetRegistration = {
  [Id in CatalogWidgetId]: WidgetRegistrationFor<Id>;
}[CatalogWidgetId];

const WIDGET_REGISTRY = [
  { id: 'buddy-widget', bundle: 'buddy-widget.min.js', mount: 'buddy', prefixMatch: false },
  {
    id: 'buddy-marketplace',
    bundle: 'marketplace-widget.min.js',
    mount: 'marketplace',
    prefixMatch: false,
  },
  {
    id: 'buddy-leaderboard',
    bundle: 'leaderboard-widget.min.js',
    mount: 'leaderboard',
    prefixMatch: false,
  },
  { id: 'buddy-badges', bundle: 'badges-widget.min.js', mount: 'badges', prefixMatch: false },
  { id: 'buddy-streak', bundle: 'streak-widget.min.js', mount: 'streak', prefixMatch: true },
  { id: 'buddy-path', bundle: 'path-widget.min.js', mount: 'path', prefixMatch: true },
  { id: 'buddy-tokens', bundle: 'tokens-widget.min.js', mount: 'tokens', prefixMatch: false },
  { id: 'buddy-kudos', bundle: 'kudos-widget.min.js', mount: 'kudos', prefixMatch: false },
  {
    id: 'buddy-group-quest',
    bundle: 'group-quest-widget.min.js',
    mount: 'group-quest',
    prefixMatch: false,
  },
  { id: 'buddy-feed', bundle: 'feed-widget.min.js', mount: 'feed', prefixMatch: false },
  {
    id: 'buddy-mystery-box',
    bundle: 'mystery-box-widget.min.js',
    mount: 'mystery-box',
    prefixMatch: false,
  },
  { id: 'buddy-league', bundle: 'league-widget.min.js', mount: 'league', prefixMatch: false },
  {
    id: 'buddy-council',
    bundle: 'council-widget.min.js',
    mount: 'council',
    prefixMatch: false,
  },
  {
    id: 'buddy-hexad-survey',
    bundle: 'hexad-survey-widget.min.js',
    mount: 'hexad-survey',
    prefixMatch: false,
  },
] as const satisfies readonly WidgetRegistration[];

/** Internal per-widget identity — the legacy DOM ids of registered widgets. */
type WidgetId = (typeof WIDGET_REGISTRY)[number]['id'];

// Compile-time drift guard: `satisfies` above already rejects unknown or
// mis-paired entries; this rejects *missing* ones. When @hatched/shared
// WIDGET_CATALOG gains a widget, this alias stops compiling until the
// registry registers it. (Runtime cross-check lives in
// apps/api/src/common/widget-catalog-consistency.spec.ts.)
type ExpectNever<T extends never> = T;
type _EveryCatalogWidgetIsRegistered = ExpectNever<
  Exclude<CatalogWidgetId, (typeof WIDGET_REGISTRY)[number]['mount']>
>;

const shadowRoots = new Map<HTMLElement, ShadowRoot>();
const mountCleanups = new WeakMap<HTMLElement, WidgetCleanup>();

function findMountElements(reg: WidgetRegistration): HTMLElement[] {
  const elements: HTMLElement[] = [];
  const seen = new Set<HTMLElement>();

  const add = (el: HTMLElement | null) => {
    if (!el || seen.has(el)) return;
    seen.add(el);
    elements.push(el);
  };

  if (!reg.prefixMatch) {
    add(document.getElementById(reg.id));
  } else {
    // Prefix-match: select every element whose id starts with the widget id.
    // Used for widgets that can appear multiple times on the same page (streak
    // with multiple admin-defined keys). Each mount div must carry its own
    // identity via a `data-*` attribute (e.g. `data-streak-key="daily"`).
    const all = document.querySelectorAll<HTMLElement>(`[id^="${reg.id}"]`);
    for (const el of Array.from(all)) {
      // Exclude prefix collisions with other widget ids (none today, but
      // keeping this check future-proofs us).
      let collides = false;
      for (const other of WIDGET_REGISTRY) {
        if (other.id === reg.id) continue;
        if (el.id === other.id) {
          collides = true;
          break;
        }
      }
      if (!collides) add(el);
    }
  }

  // Public mount contract. The legacy widget ids are still supported as
  // aliases so older snippets continue to work while docs move to
  // data-hatched-mount="buddy" style mounts.
  for (const name of [reg.mount, reg.id]) {
    const matches = document.querySelectorAll<HTMLElement>(
      `[data-hatched-mount="${name}"]`,
    );
    for (const el of Array.from(matches)) {
      add(el);
    }
  }

  return elements;
}

function readMountAttributes(el: HTMLElement): Record<string, string> {
  const out: Record<string, string> = {};
  for (const attr of Array.from(el.attributes)) {
    if (!attr.name.startsWith('data-')) continue;
    // Skip our own bookkeeping attributes.
    if (
      attr.name === 'data-hatched-mount' ||
      attr.name === 'data-hatched-mounted' ||
      attr.name === 'data-hatched-token' ||
      attr.name === 'data-hatched-config'
    )
      continue;
    // data-streak-key → streakKey
    const camel = attr.name.slice(5).replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
    out[camel] = attr.value;
  }
  return out;
}

/**
 * Resolve a per-mount widget size from `data-hatched-size` on the mount div.
 * Hosted surfaces lay out widgets at different sizes per cell, so each mount
 * carries its own size — this overrides the loader's single global
 * script-tag `data-size` for that one widget. Widgets read `config.size`, so
 * the per-mount value must land there (not in `config.mount`, which no widget
 * inspects for sizing).
 *
 * The hosted-surface layout has four sizes (`small | medium | large | wide`);
 * the widget render contract has three (`small | medium | large`). `wide` is a
 * shell grid-span proportion only, so it maps to the widget's largest render,
 * `large`. Unknown/absent → no override (keep the global size).
 */
function resolveMountSize(
  mount: Record<string, string>,
): HatchedWidgetConfig['size'] | undefined {
  // data-hatched-size → hatchedSize (see readMountAttributes camel-casing).
  const raw = mount.hatchedSize;
  switch (raw) {
    case 'small':
    case 'medium':
    case 'large':
      return raw;
    case 'wide':
      return 'large';
    default:
      return undefined;
  }
}

const CELEBRATE_BUNDLE = 'celebrate-host.min.js';

let celebrateModulePromise: Promise<CelebrateHostModule> | null = null;
let celebrateQueue: CelebrationInput[] = [];

function loadCelebrateModule(config: HatchedWidgetConfig): Promise<CelebrateHostModule> {
  if (celebrateModulePromise) return celebrateModulePromise;
  const url = withBuildId(config.cdnBaseUrl + CELEBRATE_BUNDLE);
  celebrateModulePromise = import(/* @vite-ignore */ url)
    .then((mod) => mod as CelebrateHostModule)
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[Hatched] Failed to load celebrate host bundle:', url, err);
      celebrateModulePromise = null;
      throw err;
    });
  celebrateModulePromise.then((mod) => {
    // Read the canonical config (not the captured `config`) so the ceremony
    // picks up brand theme vars that may have landed via a live-theme fetch
    // while the bundle was downloading.
    const live = window.__HATCHED_WIDGET__?.config ?? config;
    mod.ensureCelebrationHost(live.theme, live.themeVars).then(() => {
      const queued = celebrateQueue;
      celebrateQueue = [];
      for (const event of queued) mod.celebrate(event);
    });
  });
  return celebrateModulePromise;
}

function celebrateEvent(event: CelebrationInput, config: HatchedWidgetConfig): void {
  // Buffer events fired before the lazy bundle has resolved so nothing
  // silently drops. Once loaded, the queue drains in order.
  if (!celebrateModulePromise) {
    celebrateQueue.push(event);
    void loadCelebrateModule(config);
    return;
  }
  celebrateModulePromise.then((mod) => mod.celebrate(event));
}

function getScriptTag(): HTMLScriptElement | null {
  // Find our script tag by looking for one with data-embed-token or data-session-token
  const scripts = document.querySelectorAll('script[data-embed-token], script[data-session-token]');
  // WP7 (D3) — the loader resolves ONE global config; the page must carry a
  // single token-bearing script tag with every mount sharing it. Pasting a
  // second per-widget snippet silently overrides the first (last tag wins,
  // and without `defer` even that is load-order roulette). Warn loudly so
  // the integrator sees why a widget "uses the wrong token".
  if (scripts.length > 1) {
    // eslint-disable-next-line no-console
    console.warn(
      `[Hatched] Found ${scripts.length} token-bearing <script> tags on this page. ` +
        'The loader reads its config from ONE script tag (the last one in the DOM); ' +
        'the others are ignored. Use a single script tag per page and add a ' +
        '<div data-hatched-mount="…"> per widget instead.',
    );
  }
  if (scripts.length > 0) return scripts[scripts.length - 1] as HTMLScriptElement;
  // Fallback: currentScript (available during synchronous execution)
  return document.currentScript as HTMLScriptElement | null;
}


function parseThemeVars(value: string | null | undefined): Record<string, string> | undefined {
  if (!value) return undefined;
  try {
    const parsed = JSON.parse(value) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return undefined;
    const out: Record<string, string> = {};
    for (const [key, raw] of Object.entries(parsed)) {
      if (!/^--hw-[a-z0-9-]+$/i.test(key) || typeof raw !== 'string') continue;
      out[key] = raw;
    }
    return Object.keys(out).length > 0 ? out : undefined;
  } catch {
    return undefined;
  }
}

function readCustomCss(script: HTMLScriptElement | null): string | undefined {
  const inline = script?.getAttribute('data-custom-css')?.trim();
  if (inline) return inline;
  const cssId = script?.getAttribute('data-custom-css-id')?.trim();
  if (!cssId) return undefined;
  const node = document.getElementById(cssId);
  const css = node?.textContent?.trim();
  return css || undefined;
}

const SURFACE_VALUES = new Set(['paper', 'glass', 'metal', 'crt', 'parchment', 'vellum']);
const GEOMETRY_VALUES = new Set(['rounded', 'cut', 'pill', 'sharp', 'organic']);
const MOTION_PROFILE_VALUES = new Set(['calm', 'standard', 'expressive', 'theatrical']);
const REWARD_VOICE_VALUES = new Set(['quiet', 'crisp', 'joyful', 'epic']);
const ICONOGRAPHY_VALUES = new Set([
  'geometric',
  'hand-drawn',
  'pixel',
  'embossed',
  'flat-mono',
]);
const TYPOGRAPHY_VALUES = new Set([
  'sans-serif-modern',
  'serif-classic',
  'mono-tech',
  'rounded-playful',
  'display-condensed',
]);

function readPersonality(script: HTMLScriptElement | null): WidgetPersonalityConfig | undefined {
  if (!script) return undefined;
  const surface = script.getAttribute('data-surface') ?? '';
  const geometry = script.getAttribute('data-geometry') ?? '';
  const motionProfile = script.getAttribute('data-motion-profile') ?? '';
  const rewardVoice = script.getAttribute('data-reward-voice') ?? '';
  const iconography = script.getAttribute('data-iconography') ?? '';
  const typography = script.getAttribute('data-typography') ?? '';
  // All six axes must be present and known. Otherwise treat as undefined and
  // let the live theme endpoint backfill them. Partial personality breaks
  // the visual coherence — the whole point is the *composition*.
  if (
    !SURFACE_VALUES.has(surface) ||
    !GEOMETRY_VALUES.has(geometry) ||
    !MOTION_PROFILE_VALUES.has(motionProfile) ||
    !REWARD_VOICE_VALUES.has(rewardVoice) ||
    !ICONOGRAPHY_VALUES.has(iconography) ||
    !TYPOGRAPHY_VALUES.has(typography)
  ) {
    return undefined;
  }
  return {
    surface: surface as WidgetPersonalityConfig['surface'],
    geometry: geometry as WidgetPersonalityConfig['geometry'],
    motion_profile: motionProfile as WidgetPersonalityConfig['motion_profile'],
    reward_voice: rewardVoice as WidgetPersonalityConfig['reward_voice'],
    iconography: iconography as WidgetPersonalityConfig['iconography'],
    typography_pair: typography as WidgetPersonalityConfig['typography_pair'],
  };
}

function readConfig(script: HTMLScriptElement | null): HatchedWidgetConfig {
  const embedToken = script?.getAttribute('data-embed-token') ?? '';
  const sessionToken = script?.getAttribute('data-session-token') ?? '';
  const token = sessionToken || embedToken;

  const requestedTheme = script?.getAttribute('data-theme');
  const theme: 'light' | 'dark' = requestedTheme === 'dark' ? 'dark' : 'light';
  const requestedSize = script?.getAttribute('data-size');
  const size =
    requestedSize === 'small' || requestedSize === 'medium' || requestedSize === 'large'
      ? requestedSize
      : undefined;
  const lang = script?.getAttribute('data-lang') ?? undefined;
  const themeVars = parseThemeVars(script?.getAttribute('data-theme-vars'));
  const customCss = readCustomCss(script);
  const personality = readPersonality(script);
  const mode = sessionToken ? 'interactive' : 'read-only';

  // data-api-base-url is convenient for staging/preview, but accepting any
  // value here means a host-page XSS can siphon the bearer to attacker
  // infra. Whitelist the API origins; otherwise fall back to the path-derived
  // default so a `cdn.…/staging/widget.js` install talks to staging API even
  // without an explicit override.
  const inferredDefault = inferDefaultApiBaseUrl(script?.src);
  const requestedApiBase = script?.getAttribute('data-api-base-url')?.trim();
  let apiBaseUrl = inferredDefault;
  if (requestedApiBase) {
    if (isAllowedApiBaseUrl(requestedApiBase)) {
      apiBaseUrl = requestedApiBase;
    } else {
      // Don't fail silently — an integrator who set data-api-base-url and got
      // the default instead has no way to know why. Tell them, and what's OK.
      // eslint-disable-next-line no-console
      console.warn(
        `[Hatched] Ignoring data-api-base-url="${requestedApiBase}" — not an allowed API origin. ` +
          `Using "${inferredDefault}" instead. Allowed: ${[...ALLOWED_API_ORIGINS].join(', ')}, ` +
          `or a localhost origin for local development.`,
      );
    }
  }

  // Derive bundle base from the loader URL. A loader at `/widget.js` loads
  // bundles from `/widget/v1/`; a prefixed loader at `/staging/widget.js`
  // loads bundles from `/staging/widget/v1/`.
  let cdnBaseUrl = 'https://cdn.hatched.live/widget/v1/';
  if (script?.src) {
    try {
      const url = new URL(script.src);
      if (url.pathname.endsWith('/widget.js')) {
        cdnBaseUrl = `${url.origin}${url.pathname.replace(/\/widget\.js$/, '/widget/v1/')}`;
      } else {
        cdnBaseUrl = `${url.origin}${url.pathname.replace(/\/[^/]*$/, '/')}`;
      }
    } catch {
      // keep default
    }
  }

  return {
    token,
    theme,
    mode,
    apiBaseUrl,
    cdnBaseUrl,
    size,
    lang,
    themeVars,
    customCss,
    personality,
  };
}

function configSignature(config: HatchedWidgetConfig): string {
  return JSON.stringify({
    token: config.token,
    theme: config.theme,
    mode: config.mode,
    apiBaseUrl: config.apiBaseUrl,
    size: config.size,
    lang: config.lang,
    themeVars: config.themeVars,
    customCss: config.customCss,
    personality: config.personality,
    mount: config.mount,
  });
}

function getOrCreateShadow(el: HTMLElement): ShadowRoot {
  const existing = shadowRoots.get(el);
  if (existing) return existing;
  // attachShadow can only be called once per element in the same tab. If we
  // hit a re-mount after Fast Refresh, try to reuse any shadow TypeScript
  // doesn't know about via (el.shadowRoot ??) — but closed shadow DOM won't
  // expose .shadowRoot, so we fall through and let attachShadow throw for the
  // caller to handle.
  const shadow = el.attachShadow({ mode: 'closed' });
  shadowRoots.set(el, shadow);
  return shadow;
}

function mountSelector(widgetId: WidgetId): string {
  return `[data-hatched-mount="${widgetId}"]`;
}

function cleanupMount(mount: HTMLElement): void {
  const cleanup = mountCleanups.get(mount);
  if (cleanup) {
    cleanup();
    mountCleanups.delete(mount);
    return;
  }

  mount.replaceChildren();
}

function pruneDuplicateMounts(shadow: ShadowRoot, widgetId: WidgetId): void {
  const mounts = Array.from(shadow.querySelectorAll<HTMLElement>(mountSelector(widgetId)));

  for (const duplicate of mounts.slice(1)) {
    cleanupMount(duplicate);
    duplicate.remove();
  }
}

function getOrCreateMount(shadow: ShadowRoot, widgetId: WidgetId): HTMLElement {
  const mounts = Array.from(shadow.querySelectorAll<HTMLElement>(mountSelector(widgetId)));
  let mount = mounts[0];

  pruneDuplicateMounts(shadow, widgetId);

  if (!mount) {
    mount = document.createElement('div');
    mount.setAttribute('data-hatched-mount', widgetId);
    shadow.appendChild(mount);
  } else {
    cleanupMount(mount);
  }

  return mount;
}

let mountGenerationCounter = 0;
const renderBeaconKeys = new Set<string>();

function recordWidgetRendered(config: HatchedWidgetConfig, widgetId: WidgetId): void {
  if (!config.token) return;
  const key = `${config.apiBaseUrl}|${config.token}`;
  if (renderBeaconKeys.has(key)) return;
  renderBeaconKeys.add(key);

  const body = JSON.stringify({
    widgets: [widgetId],
    loader_version: __HATCHED_LOADER_VERSION__,
    build_id: __HATCHED_BUILD_ID__,
  });

  void fetch(`${config.apiBaseUrl.replace(/\/$/, '')}/widget/rendered`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${config.token}`,
    },
    body,
    keepalive: true,
  })
    .then((res) => {
      if (!res.ok) renderBeaconKeys.delete(key);
    })
    .catch(() => {
      renderBeaconKeys.delete(key);
    });
}

function loadBundle(
  shadow: ShadowRoot,
  bundleUrl: string,
  config: HatchedWidgetConfig,
  widgetId: WidgetId,
): void {
  // Create or reuse exactly one mount point inside the shadow root.
  const mount = getOrCreateMount(shadow, widgetId);

  // Codex review fix: stamp a generation token on the mount so the dynamic
  // import's resolution can detect a hot-swap that happened during the
  // network round-trip. Without this, a token/config change between
  // `import()` start and the bundle resolving would mount the new bundle
  // with the *old* config (token, theme, mount data attrs).
  const generation = String((mountGenerationCounter += 1));
  mount.setAttribute('data-hatched-generation', generation);
  const expectedConfigSig = configSignature(config);

  // Dynamic import from the loader closure — we already have a direct
  // reference to the mount element, so we don't need to re-discover it
  // via document.currentScript (which is always null inside module scripts).
  // The /* @vite-ignore */ marker keeps Vite from trying to analyze the URL
  // at build time.
  import(/* @vite-ignore */ bundleUrl)
    .then((mod: WidgetModule) => {
      if (!mod || typeof mod.mount !== 'function') return;
      // Hot-swap detection — the mount may have been replaced or re-stamped
      // while the bundle was loading. The `data-hatched-generation` attr is
      // the cheap fingerprint; if it changed, a newer loadBundle owns this
      // mount and we silently abandon this resolution.
      if (mount.getAttribute('data-hatched-generation') !== generation) return;
      // Element-level config drift — the host element's stamped config
      // signature must still match what we captured at start-of-fetch.
      const rootNode = mount.getRootNode();
      const hostEl =
        rootNode instanceof ShadowRoot
          ? (rootNode.host as HTMLElement)
          : null;
      const stampedSig = hostEl?.getAttribute('data-hatched-config');
      if (stampedSig && stampedSig !== expectedConfigSig) return;
      // Mount must still be in the DOM — if the host element was removed
      // (e.g. SPA route change tearing down the embed) skip mounting.
      if (!mount.isConnected) return;

      const cleanup = mod.mount(mount, config);
      recordWidgetRendered(config, widgetId);
      if (typeof cleanup === 'function') {
        mountCleanups.set(mount, cleanup);
      }
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[Hatched] Failed to load widget bundle:', bundleUrl, err);
    });
}

function mountWidgets(config: HatchedWidgetConfig): void {
  for (const reg of WIDGET_REGISTRY) {
    const elements = findMountElements(reg);
    for (const el of elements) {
      // Pass per-mount data-* attributes into the widget config.
      const mount = readMountAttributes(el);
      // Per-mount `data-hatched-size` overrides the global script-tag size for
      // this one widget (hosted surfaces size each cell independently).
      const mountSize = resolveMountSize(mount);
      const perMountConfig: HatchedWidgetConfig = {
        ...config,
        ...(mountSize ? { size: mountSize } : {}),
        mount,
      };

      // Hot-swap: if this element is already mounted with a different token,
      // tear down its shadow content and re-mount with the new token. Skip
      // cleanly if the token hasn't changed (StrictMode double-effect,
      // accidental re-init).
      if (el.hasAttribute('data-hatched-mounted')) {
        const mountedToken = el.getAttribute('data-hatched-token') ?? '';
        const mountedConfig = el.getAttribute('data-hatched-config') ?? '';
        const nextConfig = configSignature(perMountConfig);
        const shadow = shadowRoots.get(el);
        if (mountedToken === config.token && mountedConfig === nextConfig) {
          if (shadow) pruneDuplicateMounts(shadow, reg.id);
          continue;
        }
        if (shadow) {
          for (const mount of shadow.querySelectorAll<HTMLElement>('[data-hatched-mount]')) {
            cleanupMount(mount);
          }
        }
        el.setAttribute('data-hatched-token', config.token);
        el.setAttribute('data-hatched-config', nextConfig);
        try {
          const reusedShadow = getOrCreateShadow(el);
          const bundleUrl = withBuildId(config.cdnBaseUrl + reg.bundle);
          loadBundle(reusedShadow, bundleUrl, perMountConfig, reg.id);
        } catch {
          // Re-attach failures are fatal for this element; leave it blank.
        }
        window.__HATCHED_WIDGET__.mounted.add(reg.id);
        continue;
      }

      // First mount. Flag synchronously so concurrent DOMContentLoaded /
      // StrictMode re-entries hit the hot-swap branch above instead of racing.
      el.setAttribute('data-hatched-mounted', 'true');
      el.setAttribute('data-hatched-token', config.token);
      el.setAttribute('data-hatched-config', configSignature(perMountConfig));

      let shadow: ShadowRoot;
      try {
        shadow = getOrCreateShadow(el);
      } catch {
        continue;
      }
      const bundleUrl = withBuildId(config.cdnBaseUrl + reg.bundle);
      loadBundle(shadow, bundleUrl, perMountConfig, reg.id);
      window.__HATCHED_WIDGET__.mounted.add(reg.id);
    }
  }
}

function destroyWidgets(): void {
  for (const reg of WIDGET_REGISTRY) {
    for (const el of findMountElements(reg)) {
      const shadow = shadowRoots.get(el);
      if (shadow) {
        for (const mount of shadow.querySelectorAll<HTMLElement>('[data-hatched-mount]')) {
          cleanupMount(mount);
        }
        shadow.innerHTML = '';
      }
      el.removeAttribute('data-hatched-mounted');
      el.removeAttribute('data-hatched-token');
      el.removeAttribute('data-hatched-config');
    }
  }
  window.__HATCHED_WIDGET__.mounted.clear();
}

// --- Realtime infrastructure ---

let smartPollHandle: SmartPollHandle | null = null;
let crossTabBridge: CrossTabBridge | null = null;
let stopOpsWatcher: (() => void) | null = null;

function stopInfrastructure(): void {
  smartPollHandle?.stop();
  smartPollHandle = null;
  crossTabBridge?.close();
  crossTabBridge = null;
  stopOpsWatcher?.();
  stopOpsWatcher = null;
}

function startInfrastructure(config: HatchedWidgetConfig): void {
  stopInfrastructure();
  if (!config.token) return;
  const store = window.__HATCHED_WIDGET__.store;

  crossTabBridge = attachCrossTabBridge(store, {
    apiBaseUrl: config.apiBaseUrl,
    token: config.token,
  });

  smartPollHandle = startSmartPoll({
    url: `${config.apiBaseUrl.replace(/\/$/, '')}/widget/state`,
    token: config.token,
    baseInterval: 5_000,
    maxInterval: 60_000,
    onFresh: (raw, _etag) => {
      const state = raw as AggregateState;
      const patch: Partial<WorkspaceState> = {
        buddy: state.buddy,
        badges: state.badges,
        ...(Array.isArray(state.scopes) ? { scopes: state.scopes } : {}),
        ...(state.attribution ? { attribution: state.attribution } : {}),
        ...(state.share ? { share: state.share } : {}),
        ...(state.feature_config && typeof state.feature_config === 'object'
          ? { featureConfig: state.feature_config as Record<string, unknown> }
          : {}),
      };
      // Reconcile pending ops. Anything the server still reports pending
      // stays in the store; anything it dropped resolves (may have been
      // completed between ticks — the async-ops-watcher would have caught
      // it too, this is just the slower safety net).
      const serverPending = new Set(state.pending_operations.map((op) => op.id));
      const currentPending = store.getState().pendingOps;
      for (const op of currentPending) {
        if (!serverPending.has(op.id)) store.resolvePending(op.id);
      }
      for (const op of state.pending_operations) {
        store.markPending({
          id: op.id,
          type: op.type,
          status: op.status,
          startedAt: Date.parse(op.updated_at) || Date.now(),
        });
      }
      store.hydrate(patch);
      crossTabBridge?.broadcastHydrate(patch);
    },
    onError: (err) => {
      // eslint-disable-next-line no-console
      if (typeof console !== 'undefined') console.debug('[Hatched] smart-poll:', err);
    },
  });

  stopOpsWatcher = startAsyncOpsWatcher({
    store,
    apiBaseUrl: config.apiBaseUrl,
    token: config.token,
    // Async ops (evolve/hatch) mutate buddy slices that /widget/state owns,
    // so when one resolves we ping smart-poll to pull the fresh snapshot
    // right away rather than waiting for the next scheduled tick.
    onResolve: () => smartPollHandle?.ping(),
  });
}

// --- Live theme fetch ---
//
// Production parity: when the operator changes anything in Widget Studio
// and saves, that mutation must reach the partner's deployed snippet
// without anyone re-pasting attributes. The script tag's data-theme-vars
// / data-custom-css / data-size attributes are treated as **fallbacks**;
// the loader fetches the authoritative theme on init and hot-swaps when
// it differs. Re-fetched on focus/visibility so the dashboard preview and
// the live page stay in lock-step.

interface LiveThemeResponse {
  widget_theme?: 'light' | 'dark' | 'custom';
  widget_size?: 'small' | 'medium' | 'large';
  widget_theme_config?: {
    vars?: Record<string, string>;
    personality?: WidgetPersonalityConfig;
  };
  widget_custom_css?: string;
}

function isPersonality(value: unknown): value is WidgetPersonalityConfig {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.surface === 'string' &&
    SURFACE_VALUES.has(v.surface as string) &&
    typeof v.geometry === 'string' &&
    GEOMETRY_VALUES.has(v.geometry as string) &&
    typeof v.motion_profile === 'string' &&
    MOTION_PROFILE_VALUES.has(v.motion_profile as string) &&
    typeof v.reward_voice === 'string' &&
    REWARD_VOICE_VALUES.has(v.reward_voice as string) &&
    typeof v.iconography === 'string' &&
    ICONOGRAPHY_VALUES.has(v.iconography as string) &&
    typeof v.typography_pair === 'string' &&
    TYPOGRAPHY_VALUES.has(v.typography_pair as string)
  );
}

let lastLiveThemeAt = 0;
const LIVE_THEME_MIN_INTERVAL_MS = 5_000;
let liveThemeRefreshWatcherInstalled = false;

async function fetchLiveTheme(config: HatchedWidgetConfig): Promise<void> {
  if (!config.token) return;
  const now = Date.now();
  if (now - lastLiveThemeAt < LIVE_THEME_MIN_INTERVAL_MS) return;
  lastLiveThemeAt = now;
  try {
    const res = await fetch(`${config.apiBaseUrl.replace(/\/$/, '')}/widget/theme`, {
      headers: { Authorization: `Bearer ${config.token}` },
      cache: 'no-cache',
    });
    if (!res.ok) return;
    const data = (await res.json()) as LiveThemeResponse;
    const overrides: Partial<HatchedWidgetConfig> = {};
    if (data.widget_theme === 'light' || data.widget_theme === 'dark') {
      overrides.theme = data.widget_theme;
    }
    if (
      data.widget_size === 'small' ||
      data.widget_size === 'medium' ||
      data.widget_size === 'large'
    ) {
      overrides.size = data.widget_size;
    }
    if (data.widget_theme_config?.vars && typeof data.widget_theme_config.vars === 'object') {
      const vars: Record<string, string> = {};
      for (const [key, value] of Object.entries(data.widget_theme_config.vars)) {
        if (/^--hw-[a-z0-9-]+$/i.test(key) && typeof value === 'string') vars[key] = value;
      }
      if (Object.keys(vars).length > 0) overrides.themeVars = vars;
    }
    if (isPersonality(data.widget_theme_config?.personality)) {
      overrides.personality = data.widget_theme_config.personality;
    }
    if (typeof data.widget_custom_css === 'string') overrides.customCss = data.widget_custom_css;
    if (Object.keys(overrides).length === 0) return;
    window.__HATCHED_WIDGET__.init(overrides);
  } catch {
    // Network noise — keep the inline attributes; nothing to do.
  }
}

function watchForLiveThemeRefresh(): void {
  if (typeof document === 'undefined') return;
  if (liveThemeRefreshWatcherInstalled) return;
  liveThemeRefreshWatcherInstalled = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      lastLiveThemeAt = 0;
      void fetchLiveTheme(window.__HATCHED_WIDGET__.config);
    }
  });
  window.addEventListener('focus', () => {
    lastLiveThemeAt = 0;
    void fetchLiveTheme(window.__HATCHED_WIDGET__.config);
  });
}

async function trackEvent(
  type: string,
  payload?: Record<string, unknown>,
): Promise<TrackResult | null> {
  const api = window.__HATCHED_WIDGET__;
  const { config, store } = api;
  if (!config.token) {
    // eslint-disable-next-line no-console
    console.warn('[Hatched] track() called before a token was supplied');
    return null;
  }
  const eventId = `evt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  try {
    const res = await fetch(`${config.apiBaseUrl.replace(/\/$/, '')}/widget/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${config.token}`,
      },
      body: JSON.stringify({
        type,
        event_id: eventId,
        properties: payload ?? {},
      }),
    });
    if (!res.ok) {
      // eslint-disable-next-line no-console
      console.error('[Hatched] track failed:', res.status);
      return null;
    }
    const data = (await res.json()) as TrackResult;
    if (data.effects) {
      store.applyEffects(data.effects);
      crossTabBridge?.broadcastEffects(data.effects);
    }
    // The event may have mutated slices we don't locally merge (e.g. a brand
    // new streak entry) — nudge smart-poll so the next tick refreshes fast.
    smartPollHandle?.ping();
    return data;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[Hatched] track failed:', err);
    return null;
  }
}

// --- Initialize ---

(function init() {
  const script = getScriptTag();
  const config = readConfig(script);
  const existing = window.__HATCHED_WIDGET__;

  // Next.js <Script> in dev (StrictMode / Fast Refresh) can re-insert the
  // tag and re-execute the bundle. Reuse the existing API so the original
  // closed shadow roots and cleanup callbacks stay reachable.
  if (existing) {
    existing.init(config);
    return;
  }

  const store = new WorkspaceStore();

  window.__HATCHED_WIDGET__ = {
    config,
    mounted: new Set(),
    store,
    init(overrides?: Partial<HatchedWidgetConfig>) {
      const merged = {
        ...window.__HATCHED_WIDGET__.config,
        ...overrides,
      };
      window.__HATCHED_WIDGET__.config = merged;
      startInfrastructure(merged);
      mountWidgets(merged);
      // If the celebrate host already bootstrapped (prewarm) before this
      // theme update landed, re-style it so the ceremony adopts the brand.
      if (celebrateModulePromise) {
        void celebrateModulePromise.then((mod) =>
          mod.ensureCelebrationHost(merged.theme, merged.themeVars),
        );
      }
      void fetchLiveTheme(merged);
      watchForLiveThemeRefresh();
    },
    destroy() {
      stopInfrastructure();
      destroyWidgets();
    },
    celebrate(event: CelebrationInput) {
      celebrateEvent(event, window.__HATCHED_WIDGET__.config);
    },
    track: trackEvent,
  };

  // Public introspection surface — `window.HatchedWidgets`. Lets tenant
  // code list widgets, list supported locales, trigger a re-mount after
  // adding mount divs to the DOM, or tear everything down on SPA exit.
  window.HatchedWidgets = {
    version: __HATCHED_LOADER_VERSION__,
    buildId: __HATCHED_BUILD_ID__,
    list() {
      return WIDGET_REGISTRY.map((reg) => ({
        name: reg.id,
        mountAttr: reg.mount,
        prefixMatch: reg.prefixMatch,
      }));
    },
    listLocales() {
      return listSupportedLocales();
    },
    remount(overrides?: Partial<HatchedWidgetConfig>) {
      window.__HATCHED_WIDGET__.init(overrides);
    },
    unmountAll() {
      window.__HATCHED_WIDGET__.destroy();
    },
  };

  // Auto-mount when DOM is ready — but only if we actually have a token.
  // The guided /demo/try flow starts token-less and calls init({ token })
  // after a successful /demo/hatch, so we shouldn't attempt a doomed mount
  // on the first tick.
  const autoMount = () => {
    if (!config.token) return;
    startInfrastructure(config);
    mountWidgets(config);
    // Fire-and-forget: pull live theme + custom CSS from the dashboard.
    // The first paint uses the script-tag attributes (no flash for partners
    // who hard-code them); the API answer hot-swaps if anything diverges.
    void fetchLiveTheme(config);
    watchForLiveThemeRefresh();
    // Prewarm the celebrate-host bundle during idle time. Without this,
    // the first celebration pays a cold-start dynamic import (~200-400ms
    // of network + parse) which makes the popup feel sluggish on the very
    // moment the player is most likely to be watching.
    const prewarm = () => {
      if (celebrateModulePromise) return;
      void loadCelebrateModule(config).catch(() => {
        /* prewarm failure is non-fatal — first celebrate() will retry */
      });
    };
    if (typeof (window as unknown as { requestIdleCallback?: unknown }).requestIdleCallback === 'function') {
      (window as unknown as {
        requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void;
      }).requestIdleCallback(prewarm, { timeout: 1500 });
    } else {
      window.setTimeout(prewarm, 600);
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoMount);
  } else {
    autoMount();
  }
})();

export {};
